import { memo, useState } from 'react';
import './leyenda.css';
import MatrizTipologia from './MatrizTipologia';
import { BotonInfo } from './Modal';
import BotonColapsar from './BotonColapsar';
import { ESQUINAS, MATRIZ_CSS } from '../tipologia';
import { ALPHAS } from '../alfas';
import { txt, cambiaIdioma, OTRO_IDIOMA } from '../i18n';

// Panel de color de la grilla base: elige que variable colorea los hexagonos
// (accesibilidad, residentes, trabajadores o tipologia) y muestra la clave del
// modo activo, una rampa para los tres primeros y la matriz bivariada para la
// tipologia. La explicacion completa de cada concepto vive en los popups.
const MODOS = [
  { clave: 'accesibilidad', etiqueta: 'comun.accesibilidad' },
  { clave: 'poblacion', etiqueta: 'comun.residentes' },
  { clave: 'trabajadores', etiqueta: 'comun.trabajadores' },
  { clave: 'tipologia', etiqueta: 'comun.tipologia' },
];

const RAMPA_CSS = {
  accesibilidad: 'linear-gradient(to right, #0c2040, #60a8ff)',
  poblacion: 'linear-gradient(to right, #281c08, #ffc440)',
  trabajadores: 'linear-gradient(to right, #280e08, #ff6e3c)',
};

function etiquetasRampa(modo, maxima) {
  if (modo === 'poblacion') {
    return ['0', txt('leyenda.residentesPorCelda'), String(Math.round(maxima?.poblacion ?? 0))];
  }
  if (modo === 'trabajadores') {
    return ['0', txt('leyenda.trabajadoresPorCelda'), String(Math.round(maxima?.trabajadores ?? 0))];
  }
  return ['0', txt('leyenda.accesibilidadRampa'), '1'];
}

function descripcion(modo) {
  if (modo === 'poblacion') return txt('leyenda.descPoblacion');
  if (modo === 'trabajadores') return txt('leyenda.descTrabajadores');
  if (modo === 'tipologia') return txt('leyenda.descTipologia');
  return txt('leyenda.descAccesibilidad');
}

// Clave de la tipologia: la matriz que colorea el mapa, con las cuatro
// esquinas nombradas debajo. Es la misma leyenda del panel A de la figura, y
// cuando hay una celda elegida resalta su casilla, para que la matriz no
// tenga que repetirse en el panel de la celda
function ClaveTipologia({ tipologia }) {
  return (
    <div className="leyenda-bivariada">
      <MatrizTipologia
        tamano="grande"
        accClass={tipologia?.accClass}
        pesoClass={tipologia?.pesoClass}
      />
      <ul className="leyenda-tipos">
        {ESQUINAS.map(({ accClass, pesoClass, nombre }) => (
          <li key={nombre}>
            <span
              className="leyenda-cuadro"
              style={{ background: MATRIZ_CSS[accClass][pesoClass] }}
            />
            <span>{nombre}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Leyenda({
  modo, onModo, alpha, onAlpha, maxima, tipologia, onInfo, onAcerca, sinMovimiento, onMovimiento,
}) {
  const [minLabel, medLabel, maxLabel] = etiquetasRampa(modo, maxima);
  const [colapsada, setColapsada] = useState(false);
  return (
    <div className={`leyenda-panel ${colapsada ? 'leyenda-panel--colapsada' : ''}`}>
      <div className="leyenda-scanlines" />
      <div className="leyenda-contenido">
        <div className="leyenda-cabecera">
          <span className="leyenda-titulo">{txt('leyenda.titulo')}</span>
          <div className="leyenda-cabecera-botones">
            <BotonInfo onClick={() => onInfo('intro')} etiqueta={txt('info.intro')} />
            <BotonColapsar
              colapsado={colapsada}
              onToggle={() => setColapsada((valor) => !valor)}
            />
          </div>
        </div>

        {!colapsada && (
          <>
            <div className="leyenda-modos">
              {MODOS.map(({ clave, etiqueta }) => (
                <button
                  key={clave}
                  type="button"
                  className={`leyenda-modo ${modo === clave ? 'leyenda-modo--activo' : ''}`}
                  onClick={() => onModo(clave)}
                >
                  {txt(etiqueta)}
                </button>
              ))}
            </div>

            {/* Alfa solo cambia el color en el modo accesibilidad, asi que vive
                dentro de ese modo y no como panel aparte */}
            {modo === 'accesibilidad' && (
              <div className="leyenda-alfas">
                <div className="leyenda-alfas-titulo">
                  <span>{txt('app.selectorAlfa')}</span>
                  <BotonInfo onClick={() => onInfo('alpha')} etiqueta={txt('info.alpha')} />
                </div>
                <div className="leyenda-alfas-botones">
                  {ALPHAS.map(({ clave, etiqueta }) => (
                    <button
                      key={clave}
                      type="button"
                      className={`leyenda-alfa ${alpha === clave ? 'leyenda-alfa--activo' : ''}`}
                      onClick={() => onAlpha(clave)}
                    >
                      α = {etiqueta}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {modo === 'tipologia' ? (
              <ClaveTipologia tipologia={tipologia} />
            ) : (
              <div className="leyenda-rampa">
                <div className="leyenda-rampa-barra" style={{ background: RAMPA_CSS[modo] }} />
                <div className="leyenda-rampa-etiquetas">
                  <span>{minLabel}</span>
                  <span>{medLabel}</span>
                  <span>{maxLabel}</span>
                </div>
              </div>
            )}

            <p className="leyenda-texto">{descripcion(modo)}</p>

            <div className="leyenda-item">
              <span className="leyenda-swatch leyenda-swatch--dorado" />
              <span>{txt('leyenda.itemPrimerOrden')}</span>
            </div>
            <div className="leyenda-item">
              <span className="leyenda-swatch leyenda-swatch--rojo" />
              <span>{txt('leyenda.itemSegundoOrden')}</span>
            </div>

            <div className="leyenda-pie">
              <button type="button" className="leyenda-acerca" onClick={onAcerca}>
                {txt('leyenda.acerca')}
              </button>
              <button
                type="button"
                className="leyenda-idioma"
                onClick={onMovimiento}
                aria-pressed={!sinMovimiento}
                aria-label={txt('leyenda.movimientoAria')}
                title={txt('leyenda.movimientoAria')}
              >
                {txt(sinMovimiento ? 'leyenda.movimientoNo' : 'leyenda.movimientoSi')}
              </button>
              {/* Cambiar de idioma recarga con ?lang=, asi que el enlace de la
                  demo puede abrir directo en uno u otro */}
              <button
                type="button"
                className="leyenda-idioma"
                onClick={() => cambiaIdioma(OTRO_IDIOMA)}
                aria-label={txt('idioma.cambiar')}
                title={txt('idioma.cambiar')}
              >
                {txt('idioma.otro')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Sus props son estables entre frames: memoizarla evita re-renderizarla
// durante la animacion del escaneo.
export default memo(Leyenda);
